// orderCancelReason.js — the BO cancel-reason list and the "who cancelled +
// why" helpers of /seller/orders (spec docs/specs/2026-10-16-seller-order-
// cancel-actor-reason.md §4/§5; owner 2026-10-16: "ต้องรู้ว่าใครเป็นคนยกเลิก
// ยกเลิกโดยลูกค้า ยกเลิกโดยพนักงาน และต้องสามารถระบุเหตุผลได้เหมือนกัน backoffice").
//
// BO_CANCEL_REASONS is the list the BO cancel modal (Backoffice/OrderTables.vue
// CancelReasonTable) has always shown — moved here so the seller picker reuses
// it instead of copying it. Ids are bo_orders.bo_cancel_reason_id.
//
// Pure helpers only (no vue import): tests/unit/orderCancelReason.spec.js
// drives them directly. Nothing here throws.

import { formatBangkok } from './sellerOrderDetail';

export const BO_CANCEL_REASONS = Object.freeze([
  Object.freeze({ ID: 0, Name: '-- เหตุผลการยกเลิก --' }),
  Object.freeze({ ID: 1, Name: 'ลูกค้าต้องการยกเลิก' }),
  Object.freeze({ ID: 2, Name: 'แอดมินต้องการยกเลิก' }),
  Object.freeze({ ID: 3, Name: 'การขนส่ง' }),
  Object.freeze({ ID: 4, Name: 'การชำระเงิน' }),
  Object.freeze({ ID: 5, Name: 'เคลมสินค้า' }),
  Object.freeze({ ID: 6, Name: 'อื่น ๆ ' })
]);

/** Claim: the BO claim flow needs photos and flips to 142 — never offered on the seller page (spec D6). */
export const CLAIM_REASON_ID = 5;

/** Max length of the free-text reason (spec §4: ≤ 255 runes). */
export const CANCEL_NOTE_MAX = 255;

function toInt(v) {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : 0;
}

function trim(v) {
  return v === null || v === undefined ? '' : String(v).trim();
}

function tr(t, key, params, fallback) {
  if (typeof t !== 'function') return fallback;
  const s = t(key, params);
  return s && s !== key ? String(s) : fallback;
}

/** The seller picker: the BO list without the placeholder (0) and claim (5); labels trimmed; a copy. */
export function sellerCancelReasons() {
  return BO_CANCEL_REASONS.filter(r => r.ID !== 0 && r.ID !== CLAIM_REASON_ID).map(r => ({ ID: r.ID, Name: trim(r.Name) }));
}

/** The BO label of a bo_cancel_reason_id ("" for 0 / unknown). */
export function cancelReasonLabel(code) {
  const n = toInt(code);
  if (n <= 0) return '';
  const hit = BO_CANCEL_REASONS.find(r => r.ID === n);
  return hit ? trim(hit.Name) : '';
}

const ACTOR_TYPES = ['customer', 'staff', 'system'];

/** cancel.by_type normalised: 'customer' | 'staff' | 'system' | '' (unknown / no object). */
export function cancelActorType(cancel) {
  if (!cancel || typeof cancel !== 'object') return '';
  const s = trim(cancel.by_type).toLowerCase();
  return ACTOR_TYPES.indexOf(s) > -1 ? s : '';
}

/**
 * cancelBadge(cancel, t) — the status-cell badge of a cancelled row:
 * { type, text, badgeClass }. null when the API sent no cancel object (old
 * API / not cancelled): the page then draws the plain status badge.
 */
export function cancelBadge(cancel, t) {
  if (!cancel || typeof cancel !== 'object') return null;
  const type = cancelActorType(cancel);
  let text;
  if (type === 'customer') {
    text = tr(t, 'seller_orders.cancel_by_customer', null, 'ยกเลิกโดยลูกค้า');
  } else if (type === 'staff') {
    const name = trim(cancel.by_name);
    text = name
      ? tr(t, 'seller_orders.cancel_by_staff_named', { name }, `ยกเลิกโดยพนักงาน (${name})`)
      : tr(t, 'seller_orders.cancel_by_staff', null, 'ยกเลิกโดยพนักงาน');
  } else if (type === 'system') {
    text = tr(t, 'seller_orders.cancel_by_system', null, 'ยกเลิกอัตโนมัติ');
  } else {
    text = tr(t, 'seller_orders.status_140', null, 'ยกเลิก');
  }
  // Same colour as the cancelled tab (sellerOrderFilters STATUS_BADGE.cancelled).
  return { type, text, badgeClass: 'badge-danger' };
}

// Machine tokens written to mdfpf_orders.cancel_reason (spec §1, D2).
const TOKEN_KEYS = {
  auto_expired_24h: 'cancel_text_auto_expired_24h',
  auto_max_attempts: 'cancel_text_auto_max_attempts',
  staff_cancel_customer_request: 'cancel_text_staff_cancel_customer_request',
  staff_cancel_store: 'cancel_text_staff_cancel_store',
  staff_cancel_shipping: 'cancel_text_staff_cancel_shipping',
  staff_cancel_payment: 'cancel_text_staff_cancel_payment',
  staff_cancel_other: 'cancel_text_staff_cancel_other'
};
const TOKEN_FALLBACK = {
  auto_expired_24h: 'ไม่ชำระเงินภายใน 24 ชั่วโมง',
  auto_max_attempts: 'ชำระเงินไม่สำเร็จเกินจำนวนครั้งที่กำหนด',
  staff_cancel_customer_request: 'ลูกค้าแจ้งยกเลิก',
  staff_cancel_store: 'ร้านค้ายกเลิก',
  staff_cancel_shipping: 'ปัญหาการขนส่ง',
  staff_cancel_payment: 'ปัญหาการชำระเงิน',
  staff_cancel_other: 'ยกเลิกโดยพนักงาน'
};

/**
 * cancelReasonText(reason, t) — the customer-visible cancel_reason as text:
 * system / staff tokens and the payment-thread sentences mapped to words;
 * anything else is the customer's own text (trimmed); "" when blank.
 */
export function cancelReasonText(reason, t) {
  const s = trim(reason);
  if (!s) return '';
  if (Object.prototype.hasOwnProperty.call(TOKEN_KEYS, s)) {
    return tr(t, `seller_orders.${TOKEN_KEYS[s]}`, null, TOKEN_FALLBACK[s]);
  }
  if (/^payment timeout\b/i.test(s)) {
    return tr(t, 'seller_orders.cancel_text_payment_timeout', null, 'หมดเวลาชำระเงิน');
  }
  const failed = s.match(/^payment failed\s*:?\s*([\s\S]*)$/i);
  if (failed) {
    const base = tr(t, 'seller_orders.cancel_text_payment_failed', null, 'ชำระเงินไม่สำเร็จ');
    const detail = trim(failed[1]);
    return detail ? `${base}: ${detail}` : base;
  }
  return s;
}

/**
 * cancelView(cancel, t) — the "การยกเลิก" block of the inline detail:
 * { byType, actor, typeLabel, reasonText, at }. A staff cancel shows the
 * staff note (internal page, spec D1) and falls back to the token text; any
 * other actor shows the customer-visible reason. null without a cancel object.
 */
export function cancelView(cancel, t) {
  const badge = cancelBadge(cancel, t);
  if (!badge) return null;
  const note = trim(cancel.note);
  const reasonText = badge.type === 'staff' && note ? note : cancelReasonText(cancel.reason, t);
  return {
    byType: badge.type,
    actor: badge.text,
    typeLabel: cancelReasonLabel(cancel.reason_code),
    reasonText,
    at: formatBangkok(cancel.cancelled_at)
  };
}

/**
 * canSellerCancel(row, detail, isFullOrder) — the cancel button shows only when
 * the API says `cancellable === true` (the loaded detail's own flag wins over
 * the list row's) AND the viewer passes Auth/isFullOrder (the POST's gate; a
 * missing role would answer 401 = logout). No field (old API) → no button.
 */
export function canSellerCancel(row, detail, isFullOrder) {
  if (isFullOrder !== true) return false;
  if (detail && typeof detail === 'object' && typeof detail.cancellable === 'boolean') {
    return detail.cancellable === true;
  }
  return !!row && row.cancellable === true;
}

/** A blank form for the cancel dialog. */
export function emptyCancelForm() {
  return { bo_cancel_reason_id: null, cancel_reason: '' };
}

/**
 * validateCancelForm(form) — "" when OK, else the i18n key of the problem, in
 * the BO modal's order: reason type first (top-level BO keys), then the text.
 */
export function validateCancelForm(form) {
  const f = form && typeof form === 'object' ? form : {};
  const id = toInt(f.bo_cancel_reason_id);
  if (!sellerCancelReasons().some(r => r.ID === id)) return 'please_select_cancel_reason';
  const text = trim(f.cancel_reason);
  if (!text) return 'please_input_cancel_reason';
  if (Array.from(text).length > CANCEL_NOTE_MAX) return 'seller_orders.cancel_note_too_long';
  return '';
}

/** The POST body (spec §4): { bo_cancel_reason_id, cancel_reason } with the text trimmed. */
export function cancelPayload(form) {
  const f = form && typeof form === 'object' ? form : {};
  return { bo_cancel_reason_id: toInt(f.bo_cancel_reason_id), cancel_reason: trim(f.cancel_reason) };
}

export default {
  BO_CANCEL_REASONS,
  CLAIM_REASON_ID,
  CANCEL_NOTE_MAX,
  sellerCancelReasons,
  cancelReasonLabel,
  cancelActorType,
  cancelBadge,
  cancelReasonText,
  cancelView,
  canSellerCancel,
  emptyCancelForm,
  validateCancelForm,
  cancelPayload
};
